import { DESTINATION_HEALTH_STATUS } from '@adtrackify/at-tracking-event-types';
import { useMemo } from 'react';

import { useIntegrationData } from '@/features/integrations/hooks/use-integration-data';
import { AGGREGATED_HEALTH_STATUS } from '../types/use-integration-health-status-enums';


interface UseIntegrationHealthStatusResult {
  status: AGGREGATED_HEALTH_STATUS;
  isLoading: boolean;
  hasUnhealthyIntegrations: boolean;
  hasUnknownIntegrations: boolean;
  isFacebookConnected: boolean;
  isGoogleConnected: boolean;
}

export function useIntegrationHealthStatus(): UseIntegrationHealthStatusResult {
  const { facebookIntegration, googleIntegration, shopifyIntegration, isLoading } =
    useIntegrationData();

  const result = useMemo(() => {
    // Check if Facebook and Google are connected (exist and enabled)
    const isFacebookConnected = !!facebookIntegration?.enabled;
    const isGoogleConnected = !!googleIntegration?.enabled;

    // If neither Facebook nor Google is connected, show not_connected status
    if (!isFacebookConnected && !isGoogleConnected) {
      return {
        status: 'not_connected' as AGGREGATED_HEALTH_STATUS,
        hasUnhealthyIntegrations: false,
        hasUnknownIntegrations: false,
        isFacebookConnected,
        isGoogleConnected,
      };
    }

    // Only include enabled integrations in health status calculations
    const integrations = [facebookIntegration, googleIntegration, shopifyIntegration].filter(
      integration => integration?.enabled
    );

    const hasUnhealthyIntegrations = integrations.some(
      integration => integration?.healthStatus?.status === DESTINATION_HEALTH_STATUS.UNHEALTHY
    );

    const hasUnknownIntegrations = integrations.some(
      integration => integration?.healthStatus?.status === DESTINATION_HEALTH_STATUS.UNKNOWN
    );

    // Priority: unhealthy > unknown > healthy
    let status: AGGREGATED_HEALTH_STATUS = AGGREGATED_HEALTH_STATUS.HEALTHY;
    if (hasUnhealthyIntegrations) {
      status = AGGREGATED_HEALTH_STATUS.UNHEALTHY;
    } else if (hasUnknownIntegrations) {
      status = AGGREGATED_HEALTH_STATUS.UNKNOWN;
    }

    return {
      status,
      hasUnhealthyIntegrations,
      hasUnknownIntegrations,
      isFacebookConnected,
      isGoogleConnected,
    };
  }, [facebookIntegration, googleIntegration, shopifyIntegration]);

  return {
    ...result,
    isLoading,
  };
}
